import * as Label from "@radix-ui/react-label";
import { useField, Field, useFormikContext } from "formik";
import { validateField } from "../../lib/formValidation";
import { getColumnClasses } from "../../lib/viewportMapper";
import { Icon } from "../Icon";

import s from "./Input.module.css";
import { ForwardedRef, forwardRef, Fragment, useRef } from "react";
import { Typo3InputPropsI } from "./Fields";
import { isMobileOrTablet } from "@/lib/isMobileOrTablet";
import { useIsomorphicLayoutEffect } from "hooks/useIsomorphicLayoutEffect";
import { useDropzone } from 'react-dropzone';
import { Button } from "../Button/Button";

interface InputPropsI {
  type?: string;
  disabled?: boolean;
}

export const BaseInput = forwardRef(
  (
    { type = "text", disabled = false, ...props }: InputPropsI,
    ref?: ForwardedRef<HTMLInputElement>
  ) => {
    return (
      <input
        type={type}
        disabled={disabled}
        className={`${s.Input}`}
        ref={ref}
        {...props}
      />
    );
  }
);
BaseInput.displayName = "BaseInput";

export const Input = ({
  identifier,
  type = "text",
  as = "input",
  label,
  name,
  validators,
  disabled = false,
  ...props
}) => {
  const [field, meta, helpers] = useField(`['${name}']`);

  return (
    <>
      {label && (
        <Label.Root
          htmlFor={identifier}
          className={`${s.Label}  ${
            meta.touched && meta.error && "!text-alert"
          } ${disabled ? "text-gray-300" : "text-gray-700"}
        ${meta.touched && !meta.error && "!text-success"}`}
        >
          {label}
          <sup>*</sup>
        </Label.Root>
      )}
      <Field
        id={identifier}
        {...field}
        validate={(value) => validateField(value, validators)}
        className={`${s.Input} mt-4 ${
          meta.touched && meta.error && "!border-alert"
        }
        ${meta.touched && !meta.error && "!border-success"}
        `}
        name={field.name}
        as={as}
        disabled={disabled}
        type={type}
        rows={props.rows}
        map={props.map}
        placeholder={props.placeholder}
      />
      {meta.touched && meta.error && (
        <div className={s.ErrorMessage}>
          <Icon type="attention" className="w-[16px] inline-block mr-3" />
          {meta.error}
        </div>
      )}
    </>
  );
};

export const Typo3Input = ({
  identifier,
  type = "text",
  as = "input",
  label,
  name,
  validators,
  properties = {},
  disabled = false,
  className = "",
  ...props
}: Typo3InputPropsI) => {
  const { gridColumnClassAutoConfiguration = {}, elementClassAttribute = "" } =
    properties;
  const { viewPorts = {} } = gridColumnClassAutoConfiguration;
  return (
    <div
      className={`${
        viewPorts && getColumnClasses(viewPorts)
      } ${elementClassAttribute} ${className} mb-4`}
    >
      <Input
        identifier={identifier}
        type={type}
        as={as}
        label={label}
        name={name}
        validators={validators}
        placeholder={properties?.fluidAdditionalAttributes?.placeholder || ""}
        disabled={disabled}
        {...props}
      />
    </div>
  );
};

export const Honeypot = ({ defaultValue, ...props }) => {
  useIsomorphicLayoutEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(
        "bbb953960a1fc23d6265ee96827e2b64",
        props.identifier
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Typo3Input
      identifier={props.identifier}
      name={props.name}
      label={props.label}
      className="hidden"
      aria-hidden={true}
      type="text"
    />
  );
};

export const DeviceTracking = ({ defaultValue, ...props }) => {
  const [field, meta, helpers] = useField(`['${props.name}']`);

  useIsomorphicLayoutEffect(() => {
    const deviceType = isMobileOrTablet() ? 2 : 1;
    helpers.setValue(deviceType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Typo3Input
      identifier={props.identifier}
      label={props.label}
      name={props.name}
      className="hidden"
      type="hidden"
      value={field.value}
    />
  );
};

export const FileUpload = ({ defaultValue, name, ...props }) => {
  const [field, meta, helpers] = useField(`['${props.name}']`);
  const fileRef = useRef(null as HTMLInputElement|null);
  const formikProps = useFormikContext();

  const dropzoneAccept = props.properties?.allowedMimeTypes.reduce((accepted, allowedMimeType:string) => {
    accepted[allowedMimeType] = [];
    return accepted;
  }, {});

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: dropzoneAccept,
    multiple: false,
    onDrop: () => {
      formikProps.setFieldTouched(`['${name}']`, true, true);
    },
    onDropAccepted: (files) => {
      formikProps.setFieldValue(
        `['${name}']`,
        files[0]
      );
    }
  });

  const { allowedMimeTypes = "*" } = props.properties;

  return (
    <Field
      name={`['${name}']`}
      {...props}
      validate={(value) => validateField(value, props.validators)}
    >
      {({ field, meta }) => {
        // the input does not get emptied by default if the formik-field is resetted,
        // so if the formik-field-value is empty, we manually clear the input as well
        if (!field.value && fileRef.current) {
          fileRef.current.value = '';
        }

        return (
          <Fragment>
            <label
              className="block mb-6 text-base font-bold text-blue-dark"
              htmlFor={props.name}
            >
              {props.label!}
            </label>
            <div
              {...getRootProps()}
              className={`
                flex flex-col items-center text-center w-[100%] p-10 text-blue-dark border border-4 border-dashed
                ${
                  field.value || isDragActive
                  ? 'border-blue-light'
                  : 'border-gray-300'
                }
                ${
                  isDragActive
                  ? 'bg-gray-100'
                  : ''
                }
                rounded-4 cursor-pointer focus:outline-none
              `}
            >
              <input
                ref={fileRef}
                {...getInputProps()}
                type="file"
                id={props.name}
                accept={allowedMimeTypes}
                onChange={(e) => {
                  formikProps.setFieldValue(
                    `['${name}']`,
                    e.currentTarget.files![0]
                  );
                }}
              />
              
              <Icon
                type="file-add"
                className="w-9 mb-4 text-orange-light"
              />
              <span
                className="font-bold mb-4"
              >Datei hier hinziehen</span>
              <span
                className="text-gray-600 mb-6"
              >oder</span>
              <Button
                variant="hollow"
                as="span"
              >Datei suchen</Button>
            </div>

            {meta.touched && meta.error && (
              <div
                className={` ${
                  meta.error ? "text-alert" : "text-success"
                } whitespace-nowrap`}
              >
                <Icon
                  type="attention"
                  className="md:w-[16px] w-9 inline-flex mr-4"
                />
                <span>
                  *Bitte laden Sie eine Datei im PDF-Format hoch.
                </span>
              </div>
            )}
          </Fragment>
        );
      }}
    </Field>
  );
};

export const CarFinanceData = ({ defaultValue, ...props }) => {
  const [field, , helpers] = useField(`['${props.name}']`);

  useIsomorphicLayoutEffect(() => {
    const storedData = localStorage.getItem("calculatorData");
    let localData = storedData ? JSON.parse(storedData) : {};

    if (!Object.keys(localData).length) {
      helpers.setValue("");
      return;
    }

    // Adjust the translation map based on the data type
    const commonTranslationMap = {
      type: "Typ",
      term: "Laufzeit",
      deposit: "Anzahlung",
      purchasePrice: "Kaufpreis",
      contractualMileage: "Vertragskilometer",
      car: "Fahrzeug-ID",
      customerType: "Kundentyp",
      calculatorType: "Rechnerart",
      schwackecode: "Schwacke-Code",
      // "Nettodarlehensbetrag" and "Gesamtrückzahlungsbetrag" are conditional
    };

    // Helper function to handle nested paths in localData
    const getNestedValue = (path, data) => {
      return path.split(".").reduce((acc, part) => acc && acc[part], data);
    };

    // Define a separate map for finance-related values
    const financeTranslationMap = {
      ...commonTranslationMap,
      "monthlyInstallmentTotalAmount.grossValue": "Brutto-Monatsrate",
      "monthlyInstallmentTotalAmount.netValue": "Netto-Monatsrate",
      // Include "Schlussrate" for financing
      "balloon.netValue": "Schlussrate",
      "balloon.grossValue": "Schlussrate",
    };

    // Define a separate map for leasing-related values
    const leasingTranslationMap = {
      ...commonTranslationMap,
      "monthlyInstallment.grossValue": "Brutto-Monatsrate",
      "monthlyInstallment.netValue": "Netto-Monatsrate",
      // "Nettodarlehensbetrag" and "Gesamtrückzahlungsbetrag" are specific to leasing
      netLoanAmount: "Nettodarlehensbetrag",
      totalRepaymentAmount: "Gesamtrückzahlungsbetrag",
    };

    // Use the correct map based on the type of calculator
    const translationMap =
      localData.type === "leasing"
        ? leasingTranslationMap
        : financeTranslationMap;

    // Translate and construct the translated data
    let translatedData = Object.entries(translationMap).reduce(
      (acc, [key, value]) => {
        const pathValue = getNestedValue(key, localData);
        acc[value] = pathValue !== undefined ? pathValue : localData[key];
        return acc;
      },
      {}
    );

    // Set the value directly without additional escaping
    helpers.setValue(JSON.stringify(translatedData, null, 2));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Typo3Input
        type="hidden"
        identifier={props.identifier}
        label={props.label}
        name={props.name}
        className=""
        value={field.value}
      />
    </>
  );
};
